import html2canvas from "html2canvas";
import { AiChatMessage, AiChatMessageType } from "@app/projects/network-assessment/provider-hub/interfaces";
import {
  APPLIED_FILTERS_PREFIX,
  PROVIDER_CLAIMS_INSIGHTS_TAB_CONTEXT,
  SUMMARIES_TAB_CONTEXT
} from "@app/projects/network-assessment/provider-hub/ai-assistant/aiChatContextHelper";

export interface ChartDataEntry {
  label: string;
  value: number;
}

const CHART_PNG_SCALE = 2;
const CHART_PNG_PADDING_PX = 24 * CHART_PNG_SCALE;

/**
 * Renders the given element to a PNG and triggers an immediate browser download.
 * Mirrors the established html2canvas export pattern used elsewhere in QES (e.g. MapExport).
 */
export const downloadChartAsPng = async (element: HTMLElement, fileName: string): Promise<void> => {
  const capturedCanvas = await html2canvas(element, {
    backgroundColor: "#ffffff", // avoid a transparent PNG
    removeContainer: true,
    scale: CHART_PNG_SCALE,     // high-DPI output, matches existing exports
  });

  // Pad the rendered raster rather than the live element, so the capture bounds
  // (fixed to the element's on-screen size) never clip the padded content.
  const paddedCanvas = document.createElement("canvas");
  paddedCanvas.width = capturedCanvas.width + CHART_PNG_PADDING_PX * 2;
  paddedCanvas.height = capturedCanvas.height + CHART_PNG_PADDING_PX * 2;
  const context = paddedCanvas.getContext("2d");
  if (context) {
    context.fillStyle = "#ffffff";
    context.fillRect(0, 0, paddedCanvas.width, paddedCanvas.height);
    context.drawImage(capturedCanvas, CHART_PNG_PADDING_PX, CHART_PNG_PADDING_PX);
  }

  const link = document.createElement("a");
  link.download = fileName;
  link.href = (context ? paddedCanvas : capturedCanvas).toDataURL("image/png");
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

export const getChartColors = (): string[] => {
  return [
    // Original brand colors
    "#58595B", "#1D65AB", "#00A6D6", "#42BA82", "#F15B40", "#FCB415", "#B4912F", "#5B579C",
    // Moderately darker shade of each brand color above, cycled through before repeating any variant
    "#144878", "#0082A6", "#31875F", "#BD4A33", "#C98F10", "#826822", "#3E3B69", "#282829",
    // Lighter shade of each brand color above, two steps toward white
    "#8EB2D5", "#80D2EA", "#A0DCC0", "#F8AD9F", "#FEDA8A", "#D9C897", "#ADABCD", "#ACACAD",
  ];
}

export const parseChartEntries = (content: string[]): ChartDataEntry[] =>
  content.slice(1).map(row => {
    const trimmedRow = row.trim();
    const pipeIndex = trimmedRow.indexOf('|');
    if (pipeIndex === -1) return { label: trimmedRow, value: 0 };
    const label = trimmedRow.slice(0, pipeIndex);
    const value = Number(trimmedRow.slice(pipeIndex + 1));
    return { label, value: isNaN(value) ? 0 : value };
  });

const CHART_CONTEXT_PREFIX = 'The previous message was displayed to the user as a';

// Worded to reference "the answer" rather than "the previous message", because on a pie chart turn
// this context message follows the chart context one rather than the answer itself.
const HOW_I_FOUND_CONTEXT_PREFIX = 'The answer above was accompanied by a "How I found this" explanation, shown to the user:';

export const PIE_OTHER_THRESHOLD = 0.05;

export const OTHER_SLICE_LABEL = "Other";

// Keeps a long tail from rendering an unreadable tooltip. The list arrives largest-first, so the
// cap always keeps the biggest contributors.
const MAX_LISTED_OTHER_CATEGORIES = 10;

/**
 * Explains which categories the collapsed "Other" slice swallowed and the threshold that collapsed
 * them. Returns "" for an empty list so callers can pass the result straight to a tooltip —
 * TooltipWrapper renders nothing for empty text.
 */
export const formatOtherCategoryExplanation = (collapsedLabels: string[], threshold: number): string => {
  if (collapsedLabels.length === 0) return "";

  // toFixed then back to Number so a whole percent reads "5%" rather than "5.0%"
  const thresholdPercent = Number((threshold * 100).toFixed(1));
  const listed = collapsedLabels.slice(0, MAX_LISTED_OTHER_CATEGORIES);
  const remaining = collapsedLabels.length - listed.length;
  const names = remaining > 0 ? [...listed, `+${remaining} more`] : listed;

  return `Includes categories each under ${thresholdPercent}% of the total: ${names.join(", ")}.`;
};

export const formatPieChartContext = (content: string[]): string => {
  const entries = parseChartEntries(content);
  const total = entries.reduce((sum, e) => sum + e.value, 0);
  const visibleEntries: ChartDataEntry[] = [];
  let otherValue = 0;

  for (const entry of entries) {
    const percent = total === 0 ? 0 : entry.value / total;
    if (percent < PIE_OTHER_THRESHOLD) {
      otherValue += entry.value;
    } else {
      visibleEntries.push(entry);
    }
  }

  if (otherValue > 0) {
    visibleEntries.push({ label: OTHER_SLICE_LABEL, value: otherValue });
  }

  const lines = visibleEntries.map(e => {
    const pct = total === 0 ? 0 : (e.value / total) * 100;
    return `${e.label}: ${e.value} (${pct.toFixed(1)}%)`;
  });
  return `${CHART_CONTEXT_PREFIX} pie chart with the following data:\n${lines.join('\n')}`;
};

export const formatHowIFoundContext = (howIFound: string): string =>
  `${HOW_I_FOUND_CONTEXT_PREFIX}\n${howIFound}`;

const INJECTED_CONTEXT_PREFIXES = [CHART_CONTEXT_PREFIX, HOW_I_FOUND_CONTEXT_PREFIX, APPLIED_FILTERS_PREFIX, SUMMARIES_TAB_CONTEXT,
  PROVIDER_CLAIMS_INSIGHTS_TAB_CONTEXT];

/**
 * Restates what the user was actually shown, as System messages the model can read back on the next
 * turn. Two things are visible to the user but absent from the answer content: a pie chart's
 * collapsed "Other" slice (computed here, so the server never saw it) and the "How I found this"
 * explanation (carried on its own field, deliberately not in the content). Without these, a follow-up
 * like "what's in Other?" or "why only cardiology?" has no referent.
 *
 * Previously injected context is stripped first, so repeated submits re-derive it instead of
 * accumulating stale copies.
 */
export const buildDisplayContextPayload = (messages: AiChatMessage[]): AiChatMessage[] =>
  messages
    .filter(msg => !(msg.messageType === AiChatMessageType.System
      && INJECTED_CONTEXT_PREFIXES.some(prefix => msg.content[0]?.startsWith(prefix))))
    .flatMap((msg): AiChatMessage[] => {
      const context: AiChatMessage[] = [];

      if (msg.messageType === AiChatMessageType.PieChart && msg.content?.length >= 2) {
        context.push({
          content: [formatPieChartContext(msg.content)],
          messageType: AiChatMessageType.System,
          timestamp: msg.timestamp,
        });
      }

      if (msg.howIFound) {
        context.push({
          content: [formatHowIFoundContext(msg.howIFound)],
          messageType: AiChatMessageType.System,
          timestamp: msg.timestamp,
        });
      }

      return [msg, ...context];
    });
